import React, { useMemo, useState } from "react";

const detectMimeTypeFromBase64 = ( base64 ) => {
    if ( !base64 ) return "application/octet-stream";
    if ( base64.startsWith( "JVBERi0" ) ) return "application/pdf";
    if ( base64.startsWith( "iVBORw0KGgo" ) ) return "image/png";
    if ( base64.startsWith( "/9j/" ) ) return "image/jpeg";
    if ( base64.startsWith( "R0lGOD" ) ) return "image/gif";
    if ( base64.startsWith( "UklGR" ) ) return "image/webp";
    return "application/octet-stream";
};

const toDataUri = ( fileData ) => {
    if ( !fileData ) return "";
    if ( fileData.startsWith( "data:" ) ) return fileData;

    const base64 = fileData.replace( /^data:.*;base64,/, "" );
    const mimeType = detectMimeTypeFromBase64( base64 );
    return `data:${mimeType};base64,${base64}`;
};

const getMimeType = ( dataUri ) => {
    const match = dataUri.match( /^data:([^;]+);base64,/ );
    return match ? match[ 1 ] : "application/octet-stream";
};

const DocumentPreview = ( { fileData, fileName = "document", buttonText = "View Document" } ) => {
    const [ isOpen, setIsOpen ] = useState( false );

    const dataUri = useMemo( () => toDataUri( fileData ), [ fileData ] );
    const mimeType = useMemo( () => getMimeType( dataUri ), [ dataUri ] );
    const isImage = mimeType.startsWith( "image/" );
    const isPdf = mimeType === "application/pdf";
    const canPreview = isImage || isPdf;

    if ( !fileData ) return <span>No Document</span>;

    return (
        <>
            <button
                type="button"
                className="hover:underline text-primary"
                onClick={() => setIsOpen( true )}
            >
                {buttonText}
            </button>

            {isOpen ? (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
                    <div className="w-full max-w-4xl rounded-lg bg-white shadow-xl">
                        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
                            <p className="text-sm font-semibold text-gray-700">Document Preview</p>
                            <button
                                type="button"
                                className="text-gray-500 hover:text-gray-700"
                                onClick={() => setIsOpen( false )}
                            >
                                Close
                            </button>
                        </div>

                        <div className="h-[70vh] overflow-auto p-4">
                            {canPreview ? (
                                isImage ? (
                                    <img src={dataUri} alt="Document preview" className="mx-auto max-h-full w-auto" />
                                ) : (
                                    <iframe title="PDF Preview" src={dataUri} className="h-full w-full border-0" />
                                )
                            ) : (
                                <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 p-6 text-sm text-gray-600">
                                    Preview is available for PDF and image files only.
                                </div>
                            )}
                        </div>

                        <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-4 py-3">
                            <a
                                href={dataUri}
                                download={fileName}
                                className="rounded border border-gray-300 px-3 py-1 text-sm text-gray-700 hover:bg-gray-100"
                            >
                                Download
                            </a>
                        </div>
                    </div>
                </div>
            ) : null}
        </>
    );
};

export default DocumentPreview;
